import { ExclamationCircleOutlined, RedoOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  Input,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  message,
  notification,
} from "antd";
import { ColumnType } from "antd/es/table";
import { useContext, useEffect, useRef, useState } from "react";
import { ContentBox } from "../../components/layout/content";
import { LoginedLayout } from "../../components/layout/loginLayout";
import { UserContext } from "../../context/userContext";
import { cuttingOrder } from "../../Data/cutting";
import { GET, GETV2, PATCH, POSTV2 } from "../../services";
import {
  CHECKER_GET_STAT,
  UPLIFT_CREATE_CUTTING_QTY,
  UPLIFT_GET_CUTTING_ITEM,
  UPLIFT_GET_CUTTING_QTY,
  UPLIFT_SUBMIT_CUTTING,
} from "../../services/path";
import CuttingModal from "./view/CuttingModal";

const CuttingPage = () => {
  const [totalOrder, setTotalOrder] = useState("0");
  const [targetOrder, setTargetOrder] = useState("0");
  const [averageTime, setAverageTime] = useState("0");
  const [targetAverageTime, setTargetAverageTime] = useState("0");
  const [isTimeGood, setIsTimeGood] = useState(false);
  const [isAchieved, setIsAchieved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [batchOrder, setBatchOrder] = useState<cuttingOrder[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [batchNo, setbatchNo] = useState("");
  const tableRef = useRef<HTMLDivElement | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<cuttingOrder | null>(null);
  const { user } = useContext(UserContext);
  const handleSearch = (value: string) => {
    //if no search value, show warning notification
    if (value.trim() === "") {
      setBatchOrder([]); // Clear previous data
      setbatchNo("");
      notification.warning({
        message: "กรุณากรอก Batch NO เพื่อค้นหา",
        duration: 4,
      });
      return;
    }

    setSearchValue(value.trim());
    fetchBatchData(searchValue.trim());
  };

  const handleGetStat = async () => {
    try {
      const response = await GETV2(CHECKER_GET_STAT("cutting"));
      setTotalOrder(response?.result?.total_order ?? 0);
      setAverageTime(response?.result?.average_time ?? 0);
      setTargetOrder(response?.result?.target_order ?? 0);
      setTargetAverageTime(response?.result?.target_average_time ?? 0);
      setIsAchieved(
        Number(response?.result?.total_order) >= Number(response?.result?.target_order),
      );
      setIsTimeGood(
        Number(response?.result?.average_time) <= Number(response?.result?.target_average_time),
      );
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: error?.message ?? "Get stat fail!",
      });
    }
  };

  const handleCacheQty = async (cuttingOrder: cuttingOrder[], batch_id: string) => {
    const cacheQty = await GET(UPLIFT_GET_CUTTING_QTY(batch_id));
    if (cacheQty && Array.isArray(cacheQty)) {
      const updatedBatchOrder = cuttingOrder.map((order) => {
        const cacheItem = cacheQty.find(
          (item) => item.sku.trim().toLowerCase() === order.sku.trim().toLowerCase(),
        );

        return {
          ...order,
          current_qty: cacheItem ? cacheItem.qty : order.show_qty,
        };
      });

      setBatchOrder(updatedBatchOrder);
    }
  };

  const fetchBatchData = async (batch_no: string) => {
    try {
      setLoading(true);
      const result: cuttingOrder[] = await GET(UPLIFT_GET_CUTTING_ITEM(batch_no, "focus"));
      if (result && Array.isArray(result) && result.length > 0) {
        const payload = result.map((item) => ({
          sku: item.sku,
          qty: item.show_qty,
        }));

        await PATCH(UPLIFT_CREATE_CUTTING_QTY(batch_no), payload);
        await handleCacheQty(result, batch_no);
        setbatchNo(batch_no);
        setSearchValue("");
      } else {
        notification.warning({
          message: "ไม่มีข้อมูลในแบทช์.",
          duration: 2,
        });
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      notification.error({
        message: "เกิดข้อผิดพลาดในการดึงข้อมูล.",
        description: "กรุณาลองใหม่อีกครั้ง",
        duration: 2,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const response = await POSTV2(UPLIFT_SUBMIT_CUTTING(batchNo), null);
      if (response.success === true) {
        message.success("ยืนยันการทำงานสำเร็จ");
        setBatchOrder([]);
        setbatchNo("");
      } else {
        message.error("พบข้อผิดพลาด");
      }
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: error?.message ?? "Submit fail!",
      });
    } finally {
      await handleGetStat();
    }
  };

  useEffect(() => {
    handleGetStat();
  }, []);

  useEffect(() => {
    if (tableRef.current) {
      const table = tableRef.current;
      const isOverflowing = table.scrollWidth > table.clientWidth;
      table.style.overflowX = isOverflowing ? "scroll" : "auto";
    }
  }, [batchOrder]);

  const cellSpan = (text: string) => {
    return (
      <div>
        <span>{text}</span>
      </div>
    );
  };

  const handleRenderCell = (record: cuttingOrder) => ({
    style: {
      backgroundColor: (record.current_qty || 0) <= 0 ? "#9FFFB7" : "transparent",
    },
  });

  const handleRenderCuttingButton = (record: cuttingOrder) => {
    if (record.cutting_id === undefined || record.cutting_id === "") {
      return (
        <Space>
          <Button
            color="danger"
            type="primary"
            disabled={true}
            style={{
              backgroundColor: "#ff4d4f",
              color: "#fff",
            }}
          >
            ไม่พบแบบฟิล์ม
          </Button>
          <span>กรุณาแจ้งทีม Ecom</span>
          <ExclamationCircleOutlined />
        </Space>
      );
    }

    return (
      <Space>
        <Button
          onClick={() => handleOnItemClick(record)}
          disabled={
            (record.current_qty || 0) <= 0 ||
            record.cutting_id === undefined ||
            record.cutting_id === "" ||
            isModalOpen
          }
          type="primary"
        >
          กดตัดฟิล์ม
        </Button>
        {record.current_qty !== null &&
          record.current_qty !== undefined &&
          record.current_qty <= 0 && (
            <Button
              icon={<RedoOutlined />}
              onClick={() =>
                handleOnItemClick({
                  ...record,
                  current_qty: 1,
                })
              }
            />
          )}
      </Space>
    );
  };

  const handleOnItemClick = async (record: cuttingOrder) => {
    // Handle cut action here
    setSelectedItem(record);
    setIsModalOpen(true);
  };

  const handleEnterAction = async (value: string) => {
    // ถ้ายังไม่มี batchNo = ให้ search ก่อน
    if (value) {
      handleSearch(value);
      return;
    }

    // ถ้ามี batch แล้ว → ให้กดตัด row แรกที่ยังตัดได้
    const nextItem = batchOrder.find(
      (item) => item.cutting_id && item.cutting_id !== "" && (item.current_qty || 0) > 0,
    );

    if (nextItem) {
      handleOnItemClick(nextItem);
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Enter") return;
      const activeElement = document.activeElement as HTMLElement;
      const tagName = activeElement?.tagName;

      // ถ้ากำลังพิมพ์ใน input / textarea → ไม่ต้องให้ window ทำงาน
      if (tagName === "INPUT" || tagName === "TEXTAREA") {
        return;
      }

      event.preventDefault();
      handleEnterAction("");
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [batchOrder, isModalOpen]);

  const columns: ColumnType<cuttingOrder>[] = [
    {
      title: "กดตัด",
      key: "cut",
      render: (text: string, record: cuttingOrder) => handleRenderCuttingButton(record),
      onCell: (record: cuttingOrder) => handleRenderCell(record),
    },
    {
      title: "ขนาด",
      key: "size",
      render(value, record, index) {
        return cellSpan(record.size);
      },
      onCell: (record: cuttingOrder) => handleRenderCell(record),
    },
    {
      title: "สี",
      key: "color",
      render(value, record, index) {
        return (
          <div
            style={{
              width: 35,
              height: 35,
              backgroundColor: record.color ?? "#000000",
            }}
          />
        );
      },
      onCell: (record: cuttingOrder) => handleRenderCell(record),
    },
    {
      title: "QTY",
      dataIndex: "current_qty",
      key: "current_qty",
      render(value, record, index) {
        return cellSpan((record.current_qty || "no current qty").toString());
      },
      onCell: (record: cuttingOrder) => handleRenderCell(record),
    },
    {
      title: "รหัสสินค้า",
      dataIndex: "sku",
      key: "sku",
      render(value, record, index) {
        return cellSpan(record.sku);
      },
      onCell: (record: cuttingOrder) => handleRenderCell(record),
    },
    {
      title: "ชื่อสินค้า",
      dataIndex: "item_name",
      key: "item_name",
      render(value, record, index) {
        return cellSpan(record.item_name);
      },
      onCell: (record: cuttingOrder) => handleRenderCell(record),
    },
  ];

  return (
    <LoginedLayout>
      <ContentBox>
        {loading && <Spin size="large" style={{ position: "absolute", top: "50%", left: "50%" }} />}
        <div
          style={{
            marginBottom: "16px",
            borderBottom: "1px solid #e8e8e8",
            paddingBottom: "8px",
          }}
        >
          <h2 style={{ marginBottom: "8px" }}>Cutting Page</h2>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <Statistic
            title={<div style={{ textAlign: "center", fontSize: 13 }}>Cutting Target</div>}
            value={totalOrder}
            suffix={`/ ${targetOrder}`}
            valueStyle={{
              fontWeight: 700,
              fontSize: 18,
              textAlign: "center",
              lineHeight: "1.2",
              color: isAchieved ? "#52c41a" : "#ff4d4f",
            }}
            style={{
              width: "100%",
              maxWidth: 240,
              margin: "6px",
              padding: "8px",
              border: `1.5px solid ${isAchieved ? "#52c41a" : "#ff4d4f"}`,
              borderRadius: 8,
              boxShadow: `0px 4px 10px ${isAchieved ? "#52c41a" : "#ff4d4f"}`,
            }}
          />
          <Statistic
            title={<div style={{ textAlign: "center", fontSize: 13 }}>Avg. Time</div>}
            value={averageTime}
            suffix={` / ${targetAverageTime} Sec.`}
            valueStyle={{
              fontWeight: 700,
              fontSize: 18,
              textAlign: "center",
              lineHeight: "1.2",
              color: isTimeGood ? "#52c41a" : "#ff4d4f",
            }}
            style={{
              width: "100%",
              maxWidth: 240,
              margin: "6px",
              padding: "8px",
              border: `1.5px solid ${isTimeGood ? "#52c41a" : "#ff4d4f"}`,
              borderRadius: 8,
              boxShadow: `0px 4px 10px ${isTimeGood ? "#52c41a" : "#ff4d4f"}`,
            }}
          />
        </div>
        <Row
          style={{
            margin: "40px 0px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "16px",
          }}
        >
          <Col>
            <div>
              <span style={{ color: "red" }}> *</span>
              <span>Batch NO: </span>
            </div>
          </Col>
          <Col>
            <Input
              style={{ width: "45vh" }}
              placeholder="Enter Batch NO"
              value={searchValue} // Set the value of the search input
              onChange={(e) => setSearchValue(e.target.value)} // Update searchValue on
              onPressEnter={(e) => handleEnterAction(e.currentTarget.value)} // Handle Enter key press
            />
          </Col>
        </Row>
        {batchNo && (
          <Row
            style={{
              margin: "20px 0px",
            }}
          >
            <Col>
              <div>
                <span>Batch NO: </span>
              </div>
            </Col>
            <Col
              style={{
                marginLeft: "5px",
              }}
            >
              <div>{" " + batchNo}</div>
            </Col>
          </Row>
        )}
        <div ref={tableRef}>
          <Table
            columns={columns}
            dataSource={batchOrder}
            pagination={false}
            scroll={{ x: 1500 }}
          />
        </div>
        <div style={{ display: "flex", justifyContent: "center", paddingTop: "20px" }}>
          <Button type="primary" onClick={handleSubmit} disabled={batchOrder.length === 0}>
            Submit
          </Button>
        </div>
        <CuttingModal
          open={isModalOpen}
          onCancel={() => {
            setIsModalOpen(false);
            fetchBatchData(batchNo);
          }}
          selectedItem={selectedItem}
          batch_id={batchNo}
          user={user}
        />
      </ContentBox>
    </LoginedLayout>
  );
};

export default CuttingPage;
